import { useState } from "react";
import { Dashboard } from "./pages/Dashboard";
import { Apontamento } from "./pages/Apontamento";
import { Planejamento } from "./pages/Planejamento";
import { PcfForm, type PcfFormModo } from "./pages/PcfForm";
import { PcfImpressao } from "./pages/PcfImpressao";
import { NovaDemanda } from "./pages/NovaDemanda";
import { NovaComplementar } from "./pages/NovaComplementar";
import { PcfEtapas } from "./pages/PcfEtapas";
import { DuplicarPcf, type OrigemCliPro } from "./pages/DuplicarPcf";
import { FilaPcf, FinalizacaoManual, ReverterCancelamento, PedidosNaoConfirmados } from "./pages/ProducaoPcf";
import { ConsultaProducao } from "./pages/ConsultaProducao";
import { AlteraTempoEtapa } from "./pages/AlteraTempoEtapa";
import { ReabrePcf } from "./pages/ReabrePcf";
import { HistoricoLote, HistoricoPcf, AcertoEstoque } from "./pages/Estoque";
import { PcfConfirmacao } from "./pages/PcfConfirmacao";
import { PcfFinalizacao } from "./pages/PcfFinalizacao";
import { PainelTv } from "./pages/PainelTv";
import { Configuracao } from "./pages/Configuracao";
import { Login } from "./pages/Login";
import { getToken, getUsuario, type Usuario } from "./lib/auth";
import { ehEmbutido } from "./lib/sso";

type View =
  | { name: "dashboard" }
  | { name: "apontamento" }
  | { name: "planejamento"; naoProgramadas?: boolean }
  | { name: "pedidos"; cliente: string }
  | { name: "pcf"; modo: PcfFormModo }
  | { name: "pcf-impressao"; numpcf: number }
  | { name: "nova-demanda" }
  | { name: "nova-complementar" }
  | { name: "pcf-etapas"; numpcf: number }
  | { name: "duplicar"; cliPro?: OrigemCliPro; voltar?: PcfFormModo }
  | { name: "producao"; tela: string }
  | { name: "consulta"; parametro: string; numpcf?: number; numped?: number; nomcli?: string };

/* Painel embutido no ERP (QA 24/10/2026): o "Sair"/"Voltar" das telas levava ao Dashboard do Painel dentro do iframe —
   e o Dashboard, com o acesso vindo do ERP, caía no login do Painel. Embutido, voltar ao Dashboard = reabrir a própria tela. */
export function App() {
  const [rodada, setRodada] = useState(0);
  return <AppTelas key={rodada} reiniciar={() => setRodada((n) => n + 1)} />;
}

function AppTelas({ reiniciar }: { reiniciar: () => void }) {
  const [usuario, setUsuario] = useState<Usuario | null>(() =>
    getToken() ? getUsuario() : null
  );
  // A estação de chão de fábrica (quiosque Electron) abre em http://servidor/#apontamento:
  // cai direto na tela de apontamento, SEM login de painel (a segurança é a senha do operador).
  const hash =
    typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
  const ehEstacao = hash === "apontamento";
  const ehConfig = hash === "config";
  // Link direto para o módulo PCF (Planejamento de Produção) — exige login do painel.
  const ehPlanejamento = hash === "planejamento" || hash === "pcf";
  // Telas do menu PRODUCAO com link direto (aba Produção do ERP): #fila, #finalizacao-manual, ...
  const TELAS_PRODUCAO = [
    "fila", "finalizacao-manual", "reverter-cancelamento", "consulta-producao",
    "altera-tempo-etapa", "reabre-pcf",
    "historico-lote", "historico-pcf", "acerto-estoque",
    "pcf-confirmacao", "pcf-finalizacao", // legado novo: pcf_confirmacao / pcf_finalizacao (22/10/2026)
  ];
  const ehProducao = TELAS_PRODUCAO.includes(hash);
  // Link direto para uma PCF (ex.: do pedido no ERP): #pcf=24061
  const pcfDireta = Number((hash.match(/^pcf[=/](\d+)$/) || [])[1] || 0);
  // Folha da PCF (R1CADPCF) direto: #imprimir=24061
  const imprimirDireto = Number((hash.match(/^imprimir[=/](\d+)$/) || [])[1] || 0);
  const [view, setViewRaw] = useState<View>(
    imprimirDireto
      ? { name: "pcf-impressao", numpcf: imprimirDireto }
      : pcfDireta
      ? { name: "pcf", modo: { kind: "carregar", numpcf: pcfDireta } }
      : ehEstacao
      ? { name: "apontamento" }
      : ehPlanejamento
        ? { name: "planejamento" }
        : ehProducao
          ? hash === "consulta-producao"
            ? { name: "consulta", parametro: "" }
            : { name: "producao", tela: hash }
          : { name: "dashboard" }
  );
  const setView = (v: View) => {
    if (v.name === "dashboard" && ehEmbutido()) reiniciar();
    else setViewRaw(v);
  };

  // Painel de configuração da conexão (roda no servidor, via #config) — sem login.
  if (ehConfig) {
    return <Configuracao />;
  }

  // TV da fábrica (painel_producao_tv): sem login, como no legado.
  if (hash === "tv") {
    return <PainelTv />;
  }

  // Estação: renderiza o apontamento direto, sem exigir login.
  if (ehEstacao) {
    return <Apontamento usuarioNome="Estação" onVoltarPainel={() => {}} modoEstacao />;
  }

  if (!usuario) {
    return <Login onSuccess={() => setUsuario(getUsuario())} />;
  }

  if (view.name === "apontamento") {
    return (
      <Apontamento
        usuarioNome={usuario.nomusu}
        onVoltarPainel={() => setView({ name: "dashboard" })}
      />
    );
  }

  if (view.name === "planejamento") {
    // Produzir abre a TELA-PCF (VENDA, do item do pedido); Nova Demanda/Complementar
    // e Duplicar abrem suas telas.
    return (
      <Planejamento
        key={view.naoProgramadas ? "np" : "pl"}
        usuario={usuario}
        naoProgramadas={view.naoProgramadas}
        onVoltar={() => setView({ name: "dashboard" })}
        onProduzir={(l) =>
          setView({
            name: "pcf",
            modo: {
              kind: "nova",
              tipo: "VENDA",
              numped: l.numped,
              sequen: l.sequen,
              codcli: l.codcli,
              codpro: l.codpro,
            },
          })
        }
        onNovaDemanda={() => setView({ name: "nova-demanda" })}
        onNovaComplementar={() => setView({ name: "nova-complementar" })}
        onDuplicar={() => setView({ name: "duplicar" })}
      />
    );
  }

  if (view.name === "producao") {
    const voltar = () => setView({ name: "dashboard" });
    if (view.tela === "fila") return <FilaPcf usuario={usuario} onVoltar={voltar} />;
    if (view.tela === "finalizacao-manual") return <FinalizacaoManual usuario={usuario} onVoltar={voltar} />;
    if (view.tela === "reverter-cancelamento") return <ReverterCancelamento usuario={usuario} onVoltar={voltar} />;
    if (view.tela === "altera-tempo-etapa") return <AlteraTempoEtapa usuario={usuario} onVoltar={voltar} />;
    if (view.tela === "reabre-pcf") return <ReabrePcf usuario={usuario} onVoltar={voltar} />;
    // ESTOQUE: c1logest / c2logest / atcadace
    if (view.tela === "historico-lote") return <HistoricoLote usuario={usuario} onVoltar={voltar} />;
    if (view.tela === "historico-pcf") return <HistoricoPcf usuario={usuario} onVoltar={voltar} />;
    if (view.tela === "acerto-estoque") return <AcertoEstoque usuario={usuario} onVoltar={voltar} />;
    if (view.tela === "pcf-confirmacao")
      return (
        <PcfConfirmacao usuario={usuario} onVoltar={voltar}
          onAlterar={(numpcf) => setView({ name: "pcf", modo: { kind: "carregar", numpcf } })}
          onImprimir={(numpcf) => setView({ name: "pcf-impressao", numpcf })} />
      );
    if (view.tela === "pcf-finalizacao")
      return <PcfFinalizacao usuario={usuario} onVoltar={voltar} onImprimir={(numpcf) => setView({ name: "pcf-impressao", numpcf })} />;
  }

  if (view.name === "pedidos") {
    return <PedidosNaoConfirmados usuario={usuario} nomcli={view.cliente} onVoltar={() => setView({ name: "dashboard" })} />;
  }

  if (view.name === "consulta") {
    return (
      <ConsultaProducao
        key={`${view.parametro}-${view.numpcf ?? ""}-${view.numped ?? ""}`}
        usuario={usuario}
        parametro={view.parametro}
        numpcf={view.numpcf}
        numped={view.numped}
        nomcli={view.nomcli}
        onVoltar={() => setView({ name: "dashboard" })}
        onEditarPcf={(numpcf) => setView({ name: "pcf", modo: { kind: "carregar", numpcf } })}
        onImprimirPcf={(numpcf) => setView({ name: "pcf-impressao", numpcf })}
      />
    );
  }

  if (view.name === "duplicar") {
    return (
      <DuplicarPcf
        usuario={usuario}
        cliPro={view.cliPro}
        onVoltar={() => setView(view.voltar ? { name: "pcf", modo: view.voltar } : { name: "planejamento" })}
        onAbrirPcf={(numpcf) => setView({ name: "pcf", modo: { kind: "carregar", numpcf } })}
        onEtapas={(numpcf) => setView({ name: "pcf-etapas", numpcf })}
      />
    );
  }

  if (view.name === "nova-demanda") {
    return (
      <NovaDemanda
        usuario={usuario}
        onVoltar={() => setView({ name: "planejamento" })}
        onAbrirPcf={(modo) => setView({ name: "pcf", modo })}
      />
    );
  }

  if (view.name === "nova-complementar") {
    return (
      <NovaComplementar
        usuario={usuario}
        onVoltar={() => setView({ name: "planejamento" })}
        onAbrirPcf={(modo) => setView({ name: "pcf", modo })}
      />
    );
  }

  if (view.name === "pcf") {
    // Incluir/Alterar/Finalizar/Cancelar gravam (Partes 2b/2c); Imprimir abre a folha.
    return (
      <PcfForm
        usuario={usuario}
        modo={view.modo}
        onVoltar={() => setView({ name: "planejamento" })}
        onImprimir={(numpcf) => setView({ name: "pcf-impressao", numpcf })}
        onEtapas={(numpcf) => setView({ name: "pcf-etapas", numpcf })}
        onAbrir={(numpcf) => setView({ name: "pcf", modo: { kind: "carregar", numpcf } })}
        onDuplicarCliPro={(cliPro) => setView({ name: "duplicar", cliPro, voltar: view.modo })}
      />
    );
  }

  if (view.name === "pcf-impressao") {
    return (
      <PcfImpressao
        numpcf={view.numpcf}
        onVoltar={() => setView({ name: "pcf", modo: { kind: "carregar", numpcf: view.numpcf } })}
      />
    );
  }

  if (view.name === "pcf-etapas") {
    return (
      <PcfEtapas
        usuario={usuario}
        numpcf={view.numpcf}
        onVoltar={() => setView({ name: "pcf", modo: { kind: "carregar", numpcf: view.numpcf } })}
      />
    );
  }

  return (
    <Dashboard
      usuario={usuario}
      onAbrirApontamento={() => setView({ name: "apontamento" })}
      onAbrirPlanejamento={() => setView({ name: "planejamento" })}
      onAbrirConsulta={(parametro, cliente, numpcf, numped) =>
        setView({ name: "consulta", parametro, nomcli: cliente, numpcf, numped })
      }
      onAbrirPedidos={(cliente) => setView({ name: "pedidos", cliente })}
      onAbrirNaoProgramadas={() => setView({ name: "planejamento", naoProgramadas: true })}
      onAbrirProducao={(tela) =>
        setView(tela === "consulta-producao" ? { name: "consulta", parametro: "" } : { name: "producao", tela })
      }
      onAbrirEtapa={(etapa, cliente) =>
        // painel_producao: o tile abre a consulta_producao com o nome da etapa e o cliente do filtro.
        setView({ name: "consulta", parametro: etapa.descricao, nomcli: cliente })
      }
    />
  );
}
